import { PAYMENT_SOURCE_TYPE_LABELS, type PaymentBatch, type PaymentBatchLine } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Dialog,
  FormField,
  MoneyInput,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Download, FileSpreadsheet, Play, Send, Trash2, X } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { Amount } from "../../../features/accounting/shared.js";
import { base64ToBlob, downloadBlob } from "../../../features/accounting/shared.js";
import { ApprovalRequestPanel } from "../../../features/approvals/request-panel.js";
import { ExpensesPage, Field, Section } from "../../../features/expenses/shared.js";
import {
  BatchStatusBadge,
  LineStatusBadge,
  useBankFilePreview,
  usePaymentBatch,
  usePaymentPermissions,
  usePaymentsCrumb,
  useRefreshPayments,
} from "../../../features/payments/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";
import { useStepUp } from "../../../lib/step-up.js";

export const Route = createFileRoute("/_app/odemeler/$id")({ component: BatchDetailPage });

function BatchDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = usePaymentsCrumb();
  const batch = usePaymentBatch(id);
  const title = batch.data?.batchNo ?? t("payments.batches.detailTitle");

  return (
    <ExpensesPage title={title} breadcrumb={[crumb, { label: title }]}>
      {() =>
        batch.error ? (
          <Alert tone="danger">{errorMessage(batch.error)}</Alert>
        ) : !batch.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <BatchDetail batch={batch.data} />
        )
      }
    </ExpensesPage>
  );
}

function BatchDetail({ batch }: { batch: PaymentBatch }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const withStepUp = useStepUp();
  const refresh = useRefreshPayments();
  const permissions = usePaymentPermissions();
  const [cancelling, setCancelling] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [showBankFile, setShowBankFile] = useState(false);
  const [reason, setReason] = useState("");
  const [editingLine, setEditingLine] = useState<PaymentBatchLine | null>(null);

  const isDraft = batch.status === "DRAFT";
  const canExecute = batch.status === "APPROVED" && permissions.canExecute;
  const failed = batch.lines.filter((line) => line.status === "FAILED");

  const act = useMutation({
    mutationFn: async (action: "submit" | "execute" | "cancel") => {
      const params = { path: { id: batch.id } };
      if (action === "submit")
        return api.POST("/payment-batches/{id}/submit", { params, body: { version: batch.version } });
      if (action === "execute")
        // Yürütme hassas bir işlemdir: gerekirse yeniden kimlik doğrulama penceresi açılır ve işlem yinelenir.
        return withStepUp(() =>
          api.POST("/payment-batches/{id}/execute", { params, body: { version: batch.version } }),
        );
      return api.POST("/payment-batches/{id}/cancel", {
        params,
        body: { reason, version: batch.version },
      });
    },
    onSuccess: async (_data, action) => {
      setCancelling(false);
      setExecuting(false);
      setReason("");
      await refresh();
      toast({ tone: "success", title: t(`payments.batches.done.${action}`) });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const removeLine = useMutation({
    mutationFn: async (lineId: string) =>
      api.DELETE("/payment-batches/{id}/lines/{lineId}", {
        params: { path: { id: batch.id, lineId } },
        body: { version: batch.version },
      }),
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("payments.batches.lineRemoved") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">{batch.batchNo}</span>
        <BatchStatusBadge status={batch.status} />
        <div className="ms-auto flex flex-wrap gap-2">
          {isDraft && permissions.canManage ? (
            <Button loading={act.isPending} onClick={() => act.mutate("submit")}>
              <Send aria-hidden="true" />
              {t("payments.batches.submit")}
            </Button>
          ) : null}
          {canExecute ? (
            <Button onClick={() => setExecuting(true)}>
              <Play aria-hidden="true" />
              {t("payments.batches.execute")}
            </Button>
          ) : null}
          {permissions.canExecute && (batch.status === "APPROVED" || batch.status === "EXECUTED") ? (
            <Button variant="outline" onClick={() => setShowBankFile(true)}>
              <FileSpreadsheet aria-hidden="true" />
              {t("payments.bankFile.title")}
            </Button>
          ) : null}
          {permissions.canManage && batch.status !== "EXECUTED" && batch.status !== "CANCELLED" ? (
            <Button variant="destructive" onClick={() => setCancelling(true)}>
              <X aria-hidden="true" />
              {t("payments.batches.cancel")}
            </Button>
          ) : null}
        </div>
      </div>

      {batch.cancelReason ? (
        <Alert tone="warning" title={t("payments.batches.cancelledTitle")}>
          {batch.cancelReason}
        </Alert>
      ) : null}
      {failed.length > 0 ? (
        <Alert tone="danger" title={t("payments.batches.failedTitle", { count: failed.length })}>
          <ul className="list-inside list-disc">
            {failed.map((line) => (
              <li key={line.id}>
                {line.description}: {line.errorMessage ?? "—"}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <Section title={t("payments.batches.summary")}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("payments.fields.paymentDate")}>{formatDate(batch.paymentDate)}</Field>
          <Field label={t("payments.fields.bankAccount")}>{batch.bankAccountName ?? "—"}</Field>
          <Field label={t("payments.fields.lineCount")}>{String(batch.lineCount)}</Field>
          <Field label={t("payments.fields.totalAmount")}>
            <Amount value={batch.totalAmount} />
          </Field>
          <Field label={t("payments.fields.executedAt")}>
            {batch.executedAt ? formatDateTime(batch.executedAt) : "—"}
          </Field>
          <Field label={t("payments.fields.description")}>{batch.description ?? "—"}</Field>
        </dl>
      </Section>

      <Section title={t("payments.batches.lines")}>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <caption className="sr-only">{t("payments.batches.lines")}</caption>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">
                  {t("payments.fields.sourceType")}
                </th>
                <th scope="col" className="px-3 py-2 text-left">
                  {t("payments.fields.description")}
                </th>
                <th scope="col" className="px-3 py-2 text-left">
                  {t("payments.fields.counterparty")}
                </th>
                <th scope="col" className="px-3 py-2 text-left">
                  {t("payments.fields.dueDate")}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {t("payments.fields.amount")}
                </th>
                <th scope="col" className="px-3 py-2 text-left">
                  {t("common.status")}
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  {t("common.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {batch.lines.map((line) => (
                <tr key={line.id} className="border-t">
                  <td className="px-3 py-2">{PAYMENT_SOURCE_TYPE_LABELS[line.sourceType]}</td>
                  <td className="px-3 py-2">{line.description}</td>
                  <td className="px-3 py-2">{line.counterpartyName ?? "—"}</td>
                  <td className="px-3 py-2">{formatDate(line.dueDate)}</td>
                  <td className="px-3 py-2 text-right">
                    <Amount value={line.amount} />
                  </td>
                  <td className="px-3 py-2">
                    <LineStatusBadge status={line.status} />
                    {line.errorMessage ? (
                      <span className="block text-xs text-destructive">{line.errorMessage}</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {/* Kısmi tutar YALNIZCA taslak partideki açık kalemde değiştirilebilir. */}
                    {isDraft && permissions.canManage && line.status === "PENDING" ? (
                      <span className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditingLine(line)}>
                          {t("payments.batches.editAmount")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t("payments.batches.removeLine")}
                          loading={removeLine.isPending}
                          onClick={() => removeLine.mutate(line.id)}
                        >
                          <Trash2 aria-hidden="true" />
                        </Button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t font-semibold">
                <td className="px-3 py-2" colSpan={4}>
                  {t("common.total")}
                </td>
                <td className="px-3 py-2 text-right">
                  <Amount value={batch.totalAmount} />
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </Section>

      {batch.approvalRequestId ? (
        <Section title={t("payments.batches.approval")}>
          <ApprovalRequestPanel requestId={batch.approvalRequestId} showSummary={false} />
        </Section>
      ) : null}

      <EditLineAmountDialog
        batch={batch}
        line={editingLine}
        onClose={() => setEditingLine(null)}
        onSaved={async () => {
          setEditingLine(null);
          await refresh();
        }}
      />

      <Dialog
        open={executing}
        onOpenChange={setExecuting}
        title={t("payments.batches.executeTitle")}
        description={t("payments.batches.executeDescription")}
      >
        <div className="grid gap-4">
          <Card className="grid gap-2 bg-muted/40 p-3 text-sm">
            <Field label={t("payments.fields.bankAccount")}>{batch.bankAccountName ?? "—"}</Field>
            <Field label={t("payments.fields.paymentDate")}>{formatDate(batch.paymentDate)}</Field>
            <Field label={t("payments.fields.totalAmount")}>
              <Amount value={batch.totalAmount} />
            </Field>
          </Card>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setExecuting(false)}>
              {t("common.cancel")}
            </Button>
            <Button loading={act.isPending} onClick={() => act.mutate("execute")}>
              {t("payments.batches.execute")}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("payments.batches.cancelTitle")}
        description={t("payments.batches.cancelDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            act.mutate("cancel");
          }}
        >
          <FormField label={t("payments.batches.cancelReason")} required>
            {(control) => (
              <Textarea
                {...control}
                required
                minLength={3}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </FormField>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setCancelling(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="destructive" loading={act.isPending}>
              {t("payments.batches.cancel")}
            </Button>
          </div>
        </form>
      </Dialog>

      <BankFileDialog batch={batch} open={showBankFile} onOpenChange={setShowBankFile} />
    </div>
  );
}

/** Kısmi tutar düzenleme: tek çağrı (`PATCH …/lines/{lineId}`); kaynak ve cari değişmez, satır no korunur. */
function EditLineAmountDialog({
  batch,
  line,
  onClose,
  onSaved,
}: {
  batch: PaymentBatch;
  line: PaymentBatchLine | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const [amount, setAmount] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () =>
      api.PATCH("/payment-batches/{id}/lines/{lineId}", {
        params: { path: { id: batch.id, lineId: line!.id } },
        body: { amount: amount!, version: batch.version },
      }),
    onSuccess: async () => {
      setAmount(null);
      await onSaved();
      toast({ tone: "success", title: t("payments.batches.amountUpdated") });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open={line !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t("payments.batches.editAmount")}
      description={t("payments.batches.editAmountDescription")}
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField label={t("payments.fields.amount")} required>
          {(control) => <MoneyInput {...control} value={amount} onValueChange={setAmount} />}
        </FormField>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={save.isPending} disabled={amount === null}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Banka dosyası: IBAN EKRANDA GÖSTERİLMEZ; atlanan satırlar yalnızca cari adı ve nedeniyle listelenir. */
function BankFileDialog({
  batch,
  open,
  onOpenChange,
}: {
  batch: PaymentBatch;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const preview = useBankFilePreview(batch.id, open);
  const download = useMutation({
    mutationFn: async () =>
      (
        await api.GET("/payment-batches/{id}/bank-file", {
          params: { path: { id: batch.id }, query: { format: "CSV" as const } },
        })
      ).data!,
    onSuccess: (file) => downloadBlob(base64ToBlob(file.contentBase64, file.mimeType), file.fileName),
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("payments.bankFile.title")}
      description={t("payments.bankFile.description")}
    >
      <div className="grid gap-4">
        {preview.error ? <Alert tone="danger">{errorMessage(preview.error)}</Alert> : null}
        {preview.data ? (
          <>
            <Card className="grid gap-1 bg-muted/40 p-3 text-sm">
              <Field label={t("payments.bankFile.includedCount")}>{String(preview.data.includedCount)}</Field>
              <Field label={t("payments.bankFile.includedAmount")}>
                <Amount value={preview.data.includedAmount} />
              </Field>
              <Field label={t("payments.bankFile.fileName")}>{preview.data.fileName}</Field>
            </Card>
            {preview.data.skipped.length > 0 ? (
              <Alert tone="warning" title={t("payments.bankFile.skippedTitle")}>
                <ul className="list-inside list-disc">
                  {preview.data.skipped.map((row) => (
                    <li key={row.lineNo}>
                      {row.counterpartyName ?? t("payments.bankFile.unknownCounterparty")}: {row.reason}
                    </li>
                  ))}
                </ul>
              </Alert>
            ) : null}
            <StatusBadge tone="info">{t("payments.bankFile.ibanMasked")}</StatusBadge>
          </>
        ) : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
          <Button
            loading={download.isPending}
            disabled={(preview.data?.includedCount ?? 0) === 0}
            onClick={() => download.mutate()}
          >
            <Download aria-hidden="true" />
            {t("payments.bankFile.download")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
